import { forwardRef, ReactNode, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '@/components/generic';
import { BUTTON_STYLES, ButtonVariant } from '@/components/generic/Button/button-config';
import { DISABLED_BUTTON_CLASSES } from '@/constants';
import { bytesToMegabytes } from '@/helpers';
import { useUploadFilePicker } from '@/hooks/useFilePicker';
import { PhotoFilmIcon } from '@/resources';
import { FilePickerType } from '@/types';

export type UploadButtonRef = {
  openFileDialog: () => Promise<void>;
};

type UploadButtonProps = {
  className?: string;
  disabled?: boolean;
  icon?: ReactNode | null;
  isUploading?: boolean;
  labelClassName?: string;
  multiple?: boolean;
  onFilesSelected: (files: File[]) => void | Promise<unknown>;
  pickerTypes: FilePickerType[];
  showMaxFileSize?: boolean;
  text: string;
  variant?: ButtonVariant;
};

const UploadButton = forwardRef<UploadButtonRef, UploadButtonProps>(
  (
    {
      className = '',
      disabled = false,
      icon,
      isUploading = false,
      labelClassName = '',
      multiple = true,
      onFilesSelected,
      pickerTypes,
      showMaxFileSize = true,
      text,
      variant
    },
    ref
  ) => {
    const [t] = useTranslation('common');

    const isDisabled = disabled || isUploading;

    const { openFilePicker, hiddenInputProps } = useUploadFilePicker({
      pickerTypes,
      multiple,
      disabled: isDisabled,
      onFilesSelected
    });

    useImperativeHandle(ref, () => ({
      openFileDialog: openFilePicker
    }));

    return (
      <div>
        <button
          type='button'
          className={`flex h-10 flex-row items-center justify-center rounded-[10px] border border-primary-blue px-4 text-sm font-medium transition-all duration-300 ${
            isDisabled ? DISABLED_BUTTON_CLASSES : 'cursor-pointer hover:shadow-primary'
          } ${variant ? BUTTON_STYLES[variant] : ''} ${className}`}
          onClick={openFilePicker}
          disabled={isDisabled}>
          <div
            className={`flex w-full flex-row items-center justify-center ${
              isDisabled ? 'pointer-events-none cursor-default opacity-60' : ''
            }`}>
            {isUploading ? (
              <div className='mr-2 size-4 overflow-hidden'>
                <Spinner />
              </div>
            ) : icon === null ? null : icon ? (
              <div>{icon}</div>
            ) : (
              <PhotoFilmIcon
                fill={
                  isDisabled ? 'var(--color-secondary-steel-blue)' : 'var(--color-primary-blue)'
                }
                className='w-6'
              />
            )}

            <div
              className={`text-sm font-bold text-primary-blue ${labelClassName} ${
                isDisabled && 'text-secondary-steel-blue'
              }`}>
              {isUploading ? t('uploading') : t(text)}
            </div>
          </div>
        </button>

        <input {...hiddenInputProps} />

        {showMaxFileSize && (
          <div className='mt-0.5 flex justify-end pr-1 text-xs opacity-50'>
            {t('maxFileSize', { size: bytesToMegabytes(window.MAX_ASSET_UPLOAD_SIZE) })}
          </div>
        )}
      </div>
    );
  }
);

UploadButton.displayName = 'UploadButton';

export { UploadButton };
